import React, { useCallback, useRef, useState } from 'react'

import MobileSelectDates from '../Common/MobileSelectDates'

import ModalBase from 'LuxKit/Modal/ModalBase'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useUpdateTrip, useTrip } from 'tripPlanner/hooks/api/trip'
import * as Mapper from 'tripPlanner/mappers'

interface Props {
  tripId: string
}

/** Just like the version in EditTripDatesTP, except this one is for use with ModalContext */
function EditTripDates(props: Props) {
  const { tripId } = props

  const { data: trip } = useTrip({ tripId })

  const { startDate, endDate } = trip ?? {}

  const initialHasDates = useRef(!!(startDate && endDate)).current

  const modalElement = useModalElementContext<{
    startDate: moment.Moment | undefined
    endDate: moment.Moment | undefined
  }>()

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { mutate, isLoading } = useUpdateTrip({
    onMutate: (): undefined => {
      setErrorMsg(undefined)
    },
    onSuccess: (updatedTripRaw) => {
      const trip = Mapper.fullTrip(updatedTripRaw)
      modalElement.resolve({ startDate: trip.startDate, endDate: trip.endDate })
      const hasDates = !!(trip.startDate && trip.endDate)
      if (initialHasDates && !hasDates) {
        showSnackbar('Dates removed', 'success')
      } else if (hasDates) {
        showSnackbar('Dates saved', 'success')
      }
    },
    onError: () => {
      setErrorMsg('Error while updating trip dates. Please try again.')
    },
  })

  const updateTripDates = useCallback(
    (startDate: moment.Moment, endDate?: moment.Moment) => {
      mutate({
        tripId,
        startDate,
        endDate: endDate ?? startDate,
      })
    },
    [mutate, tripId],
  )

  const clearTripDates = useCallback(() => {
    mutate({
      tripId,
      startDate: null,
      endDate: null,
    })
  }, [mutate, tripId])

  return (
    <ModalBase>
      <MobileSelectDates
        initialStartDate={startDate}
        initialEndDate={endDate}
        onChange={updateTripDates}
        onRemove={startDate ? clearTripDates : undefined}
        onCancel={() =>
          modalElement.resolve({
            startDate: trip?.startDate,
            endDate: trip?.endDate,
          })}
        title={startDate ? 'Edit dates' : 'Add dates'}
        doneLabel={startDate ? 'Save' : 'Add dates'}
        isSubmitting={isLoading}
        errorMsg={errorMsg}
      />
    </ModalBase>
  )
}

export default EditTripDates
